import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";
import Grid from "@material-ui/core/Grid";
import IconButton from "@material-ui/core/IconButton";
import { createStyles, makeStyles } from "@material-ui/core/styles";
import AddBoxIcon from "@material-ui/icons/AddBox";
import AutorenewIcon from "@material-ui/icons/Autorenew";
import type {
	ColDef,
	DefaultMenuItem,
	EditableCallbackParams,
	GetContextMenuItemsParams,
	GridApi,
	IContextMenuParams,
	MenuItemDef,
	RowClassParams,
	RowNode,
	SelectionChangedEvent,
} from "ag-grid-community";
import "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { debounce } from "lodash-es";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ErrorModal } from "@dexteel/mesf-core";
import FormatListBulletedSharpIcon from "@material-ui/icons/FormatListBulletedSharp";
import CustomLoadingOverlay from "../../../../../../components/loaders/LazyLoading/LazyLoading";
import { useWorkOrderModalsContext } from "../../../../context/work-order-modals.context";
import { useWorkOrderPartsActionsAndButtons } from "./hooks/useWorkOrdertPartsGridActionsAndButtons";
import { useSearchParts } from "./hooks/useSearchParts";
import { useSearchWorkOrderParts } from "./hooks/useSearchWorkOrderParts";
import { AssetAddPartsDialog } from "../asset-resources-dialogs/parts-resource/asset-add-parts-dialog";
import { PartPicker } from "./components/linker-parts/parts-picker";
import { globalDefaultColDef } from "@setup/ag-grid/mesf-theme";

const useStyles = makeStyles((theme) =>
	createStyles({
		agGridContainer: {
			position: "relative",
		},
		btnAdd: {
			position: "absolute",
			padding: 0,
			bottom: 5.5,
			right: 25,
		},
		btnCached: {
			position: "absolute",
			padding: 0,
			bottom: 8,
			left: 5,
		},
	}),
);

type Props = {
	WorkOrderId: number | null;
	newMode?: boolean;
	AssetId: number | null;
	searchAssetResources: Function;
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
	setShowAssetPartsPicker: Function;
	isLoadingResourcesList?: boolean;
	WorkOrderName: string;
	AssetName: string;
};

export const WorkOrderPartsTable = ({
	WorkOrderId,
	newMode,
	AssetId,
	searchAssetResources,
	show,
	WorkOrderName,
	AssetName,
}: Props) => {
	const classes = useStyles();
	const [openViewer, setOpenViewer] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState("");
	const [errorApi, setErrorApi] = useState("");

	const [isWorkOrderPartsLoading, setIsWorkOrderPartsLoading] =
		useState<boolean>(false);
	const [workOrderPartsError, setWorkOrderPartsError] = useState<string>("");

	const [linkPartError, setLinkPartError] = useState<string>("");
	const [removePartError, setRemovePartError] = useState<string>("");

	const [showLinkResourcesToAssetDialog, setShowLinkResourcesToAssetDialog] =
		useState<boolean>(false);

	const [localShowInheritMessage, setLocalShowInheritMessage] = useState(false);

	const [listMessage, setListMessage] = useState<string>("");

	const [openPartsPicker, setOpenPartsPicker] = useState(false);

	const {
		state: { assetParts, workOrderPartsList, partSearch, rowSelectedInPart },
		actions: {
			setShowListOfParts,
			setPartSearch,
			setRowSelectedInPart,
			setLoaderListSearchParts,
		},
	} = useWorkOrderModalsContext();

	const debouncedSearch = useRef(
		debounce((partSearch?: string) => {
			searchParts().then(() => {
				setLoaderListSearchParts(false);
			});
		}, 1000),
	).current;

	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const containerStyle = useMemo(() => ({ height: "100%", width: "100%" }), []);
	const gridStyle = useMemo(() => ({ height: "100%", width: "100%" }), []);
	const [listPartSelected, setListPartSelected] = useState<any[]>([]);
	const [columnDefs, setColumnDefs] = useState<ColDef[]>();

	const searchWorkOrderParts = useSearchWorkOrderParts({
		WorkOrderId,
		setIsWorkOrderPartsLoading,
		setWorkOrderPartsError,
	});

	// SEARCH PARTS
	const searchParts = useSearchParts({ setIsLoading, setError });

	const loadingOverlayComponent = useMemo<any>(() => {
		return CustomLoadingOverlay;
	}, []);

	const onActionsClick = (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		params: EditableCallbackParams,
	) => {
		const contextMenuParams: IContextMenuParams = {
			rowNode: params.node,
			column: params.column,
			value: params.node?.data,
			x: e.nativeEvent.clientX,
			y: e.nativeEvent.clientY,
			source: "ui",
		};
		params.api.showContextMenu(contextMenuParams);
	};

	const getRowStyle = useCallback(
		(params: RowClassParams): { [key: string]: string } | undefined => {
			if (params.data.IsAssetResource) {
				return { backgroundColor: "#e8f5e9" };
			}
		},
		[],
	);

	const {
		refresh,
		addRow,
		removeParts,
		addButton,
		saveButton,
		deleteOpen,
		setSaveButton,
		setDiscardButton,
		setAddButton,
		setDeleteButton,
		setDeleteOpen,
	} = useWorkOrderPartsActionsAndButtons({
		WorkOrderId,
		newMode,
		gridRef: gridApi,
		searchWorkOrderParts,
		setRemovePartError,
		setListMessage,
	});

	const renderColumns = () => {
		setColumnDefs([]);
		const newColumns: ColDef[] = [];
		newColumns.push({
			field: "",
			flex: 1,
			minWidth: 33,
			maxWidth: 33,
			filter: false,
			headerCheckboxSelection: true,
			checkboxSelection: true,
			headerCheckboxSelectionFilteredOnly: true,
		});
		newColumns.push({
			field: "PartName",
			headerName: "Part",
			flex: 2,
			minWidth: 170,
			maxWidth: 170,
		});
		newColumns.push({
			type: "columnProperties",
			field: "Description",
			headerName: "Description",
			flex: 4,
			minWidth: 290,
		});
		newColumns.push({
			headerName: "",
			flex: 1,
			minWidth: 65,
			maxWidth: 65,
			cellClass: "ag-columns",
			wrapText: false,
			autoHeight: false,
			cellRenderer: (params: EditableCallbackParams) => {
				return (
					<Grid
						container
						style={{
							height: "100%",
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
						}}
					>
						<Grid
							item
							xs={12}
							md={12}
							style={{
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
							}}
						>
							<Grid
								container
								spacing={1}
								style={{ justifyContent: "flex-end", minWidth: 30 }}
							>
								<Grid item xs={12} md={12}>
									<Button
										fullWidth
										style={{}}
										onClick={(e) => onActionsClick(e, params)}
									>
										<FormatListBulletedSharpIcon
											style={{ height: "auto" }}
											color="action"
										/>
									</Button>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				);
			},
		});
		setColumnDefs(newColumns);
	};

	const getContextMenuItems = useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			setRowSelectedInPart(params.node?.data);
			const rowSelectedInPartInTable = params.node?.data;
			const selectedParts = gridApi?.getSelectedNodes();

			const result: (DefaultMenuItem | MenuItemDef)[] = [];
			if (!saveButton) {
				result.push({
					name: "Add Part Link",
					action: () => setOpenPartsPicker(true),
					icon: '<i class="fas fa-plus"></i>',
				});
			}

			if (selectedParts?.length) {
				result.push({
					name: "Remove Part Link",
					action: () => {
						setRowSelectedInPart(params.node?.data);
						setDeleteOpen(true);
					},
					icon: '<i class="fas fa-trash"></i>',
				});
			}

			if (
				!selectedParts?.length &&
				rowSelectedInPartInTable?.id !== -1 &&
				rowSelectedInPartInTable
			) {
				result.push({
					name: "Remove Part Link",
					action: () => {
						setRowSelectedInPart(params.node?.data);
						setListPartSelected([]);
						setDeleteOpen(true);
					},
					icon: '<i class="fas fa-trash"></i>',
				});
			}

			if (rowSelectedInPartInTable) {
				result.push("separator");
			}

			result.push({
				name: "Refresh",
				action: () => refresh(),
				icon: '<i class="fas fa-sync-alt"></i>',
			});

			return result;
		},
		[saveButton, addRow, WorkOrderId, gridApi, rowSelectedInPart],
	);

	const onSelectionChanged = useCallback((event: SelectionChangedEvent) => {
		const rowCount = event.api.getSelectedNodes().length;
		const listPartsSelected = event.api.getSelectedNodes();
		setListPartSelected(listPartsSelected);
		rowCount > 0 ? setDeleteButton(true) : setDeleteButton(false);
	}, []);

	const openModalViewerActive = () => {
		const selectedRow: RowNode<any>[] | any = gridApi?.getSelectedNodes();
		if (selectedRow[0]!.data !== undefined) {
			setRowSelectedInPart(selectedRow[0]!.data);
			setOpenViewer(!openViewer);
		}
	};

	useEffect(() => {
		if (partSearch.length >= 3) {
			setLoaderListSearchParts(true);
			setShowListOfParts(true);
			debouncedSearch(partSearch);
		} else {
			setShowListOfParts(false);
		}
	}, [partSearch]);

	useEffect(() => {
		return () => {
			debouncedSearch.cancel();
		};
	}, [debouncedSearch]);

	useEffect(() => {
		setSaveButton(false);
		setDiscardButton(false);
		setAddButton(true);
		setDeleteButton(false);
		setDeleteOpen(false);
		setPartSearch("");
	}, [WorkOrderId]);

	useEffect(() => {
		renderColumns();
	}, []);

	useEffect(() => {
		const hasAssetResource = workOrderPartsList.some(
			(part) => part.IsAssetResource === true,
		);
		const shouldShowInheritMessage =
			(!hasAssetResource || workOrderPartsList.length === 0) &&
			assetParts.length > 0;

		if (show && newMode && shouldShowInheritMessage) {
			setLocalShowInheritMessage(true);
		} else if (!show) {
			setLocalShowInheritMessage(false);
		}
	}, [assetParts]);

	return (
		<>
			{show && (
				<div style={containerStyle}>
					<Grid container style={gridStyle} className="ag-theme-alpine">
						<Grid item md={12} xs={12} className={classes.agGridContainer}>
							<AgGridReact
								onGridReady={(params) => setGridApi(params.api)}
								rowData={workOrderPartsList}
								columnDefs={columnDefs}
								defaultColDef={globalDefaultColDef}
								headerHeight={32}
								rowSelection={"multiple"}
								animateRows={true}
								getContextMenuItems={getContextMenuItems}
								onSelectionChanged={onSelectionChanged}
								loadingOverlayComponent={loadingOverlayComponent}
								onRowDoubleClicked={openModalViewerActive}
								getRowStyle={getRowStyle}
								loading={isWorkOrderPartsLoading}
							/>
							{addButton && (
								<>
									<IconButton
										onClick={() => setOpenPartsPicker(true)}
										aria-label="add"
										color="primary"
										className={classes.btnAdd}
									>
										<AddBoxIcon style={{ width: 35, height: 35 }} />
									</IconButton>
								</>
							)}
							<IconButton
								onClick={refresh}
								aria-label="delete"
								className={classes.btnCached}
							>
								<AutorenewIcon
									style={{ width: 30, height: 30, color: "green" }}
								/>
							</IconButton>
						</Grid>
						<Dialog open={deleteOpen} aria-labelledby="responsive-dialog-title">
							<DialogTitle id="responsive-dialog-title">
								{"WARNING"}
							</DialogTitle>
							<DialogContent>
								<DialogContentText style={{ padding: 0, margin: 0 }}>
									{listPartSelected?.length === 1 ? (
										<p style={{ margin: "0" }}>
											REMOVE <b>{listPartSelected[0].data.PartName}</b> FROM
											WORK ORDER{" "}
											<b>{listPartSelected[0].data.WorkOrderName}?</b>
										</p>
									) : listPartSelected.length > 1 ? (
										<>
											<p style={{ margin: 0 }}> REMOVE:</p>
											{listPartSelected?.map((part) => (
												<p key={part.data.PartName} style={{ margin: "5px 0" }}>
													<b>⁃ {part.data.PartName}, </b>
												</p>
											))}
											from work order?
										</>
									) : rowSelectedInPart?.PartName ? (
										<p style={{ margin: "0" }}>
											REMOVE <b>{rowSelectedInPart?.PartName}</b> from work
											order?
										</p>
									) : null}
								</DialogContentText>
							</DialogContent>
							<DialogActions style={{ padding: "16px 24px" }}>
								<Button
									onClick={() => setDeleteOpen(false)}
									autoFocus
									color="default"
								>
									CANCEL
								</Button>
								<Button
									autoFocus
									color="secondary"
									onClick={() => {
										if (listPartSelected.length > 0) {
											removeParts(listPartSelected);
										} else if (rowSelectedInPart) {
											removeParts([rowSelectedInPart]);
										}
									}}
								>
									REMOVE
								</Button>
							</DialogActions>
						</Dialog>
						<AssetAddPartsDialog
							show={showLinkResourcesToAssetDialog}
							onHide={(shouldUpdate: boolean) => {
								setShowLinkResourcesToAssetDialog(false);
								if (shouldUpdate) {
									searchWorkOrderParts();
								}
							}}
							AssetId={AssetId as number}
							AssetName={AssetName as string}
							WorkOrderName={WorkOrderName as string}
							searchAssetResources={searchAssetResources}
						/>
						<PartPicker
							searchParts={searchParts}
							show={openPartsPicker}
							onHide={(shouldShow: boolean) => {
								if (shouldShow) {
									searchWorkOrderParts();
								}
								setOpenPartsPicker(false);
							}}
							WorkOrderId={WorkOrderId as number}
							isLoading={isLoading}
							setShowLinkResourcesToAssetDialog={
								setShowLinkResourcesToAssetDialog
							}
						/>
					</Grid>
					<ErrorModal error={error} onHide={() => setError("")} />
					<ErrorModal error={errorApi} onHide={() => setErrorApi("")} />
					<ErrorModal
						error={workOrderPartsError}
						onHide={() => setWorkOrderPartsError("")}
					/>
					<ErrorModal
						error={linkPartError}
						onHide={() => setLinkPartError("")}
					/>
					<ErrorModal
						error={removePartError}
						onHide={() => setRemovePartError("")}
					/>
					<ErrorModal error={listMessage} onHide={() => setListMessage("")} />
				</div>
			)}
		</>
	);
};
